/**
 * Plain-language labels for audit actions that have one. Every other
 * action keeps rendering as its code (`lift.recover`, …); this is an
 * allowlist, not a translation layer, and an unknown code is never
 * guessed at.
 */
import type { AuditEntry, TaskRequest } from '../../api/v1';
import { normaliseRole } from '../../auth/roles';
import { previewRoleLabel } from '../../auth/role-preview';
import { describeRule } from '../Tasks/rrule';
import { isKnownTimeZone, formatZonedDate, formatZonedDateTime, zonedParts } from '../Tasks/zoned-time';
import { formatDateTime } from '../../utils/date-format';
import { CATEGORY_LABEL, categoryOf } from '../Tasks/calendar-categories';
import { titleOf } from '../Tasks/calendar/lane-items';
import { cleanModeOfRequest } from '../Tasks/clean-modes';
import { locationFromRequest } from '../Tasks/task-location';
import { isHighPriority } from '../../api/task-priority';

function previewedLabel(target: Record<string, unknown>): string {
  const role = normaliseRole(typeof target.previewed_role === 'string' ? target.previewed_role : null);
  return role ? previewRoleLabel(role) : 'another role';
}

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function rateText(side: unknown): string | null {
  const o = (side ?? {}) as Record<string, unknown>;
  const rate = asNumber(o.rate);
  const currency = typeof o.currency === 'string' ? o.currency : '';
  return rate === null ? null : `${currency} ${rate.toFixed(2)}`.trim();
}

function days(n: unknown): string {
  const v = asNumber(n);
  return v === null ? '?' : v === 1 ? 'every day' : `every ${v} days`;
}

function zoneList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((z): z is string => typeof z === 'string') : [];
}

function zonesSentence(zones: string[]): string {
  if (zones.length <= 3) return zones.join(', ');
  return `${zones.slice(0, 3).join(', ')} and ${zones.length - 3} more`;
}

function side(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/** A go-live date as the audit row holds it: a bare `YYYY-MM-DD` (or
 *  null), or an object carrying one under `date` / `metrics_start_date`. */
function startDateOf(v: unknown): string | null {
  const iso = (x: unknown) => (typeof x === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x) ? x : null);
  if (typeof v === 'string') return iso(v);
  const o = side(v);
  return iso(o.date) ?? iso(o.metrics_start_date) ?? iso(o.metricsStartDate);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dateText(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[(m ?? 1) - 1]} ${y}`;
}

/** "front of house from every 2 days to every day", or null if unchanged. */
function targetChange(name: string, before: unknown, after: unknown): string | null {
  const b = asNumber(before);
  const a = asNumber(after);
  if (a === null) return null;
  if (b === null) return `${name} ${days(a)}`;
  if (a === b) return null;
  return `${name} from ${days(b)} to ${days(a)}`;
}

/** The site-settings rows (Metrics overhaul, 2026-10-22). Each says
 *  what it changed FROM as well as TO (#28): the old value is what a
 *  reviewer needs, and the Target column no longer carries it. */
function siteSettingsLabel(action: string, target: Record<string, unknown>): string | null {
  switch (action) {
    case 'site_settings.labour_rate': {
      const before = rateText(target.before);
      const after = rateText(target.after);
      if (after && before && before !== after) return `Changed the labour rate from ${before} to ${after} per hour`;
      if (after) return `Set the labour rate to ${after} per hour`;
      return before ? `Cleared the labour rate (was ${before} per hour; hours only)` : 'Cleared the labour rate (hours only)';
    }
    case 'site_settings.cleaning_targets': {
      const before = side(target.before);
      const after = side(target.after);
      const set = `Set cleaning targets: front of house ${days(after.foh_days)}, back of house ${days(after.boh_days)}`;
      // No earlier value on the row: say what it is now.
      if (asNumber(before.foh_days) === null && asNumber(before.boh_days) === null) return set;
      const parts = [
        targetChange('front of house', before.foh_days, after.foh_days),
        targetChange('back of house', before.boh_days, after.boh_days),
      ].filter((p): p is string => p !== null);
      return parts.length ? `Changed cleaning targets: ${parts.join('; ')}` : set;
    }
    case 'site_settings.metrics_start_date': {
      const before = startDateOf(target.before);
      const after = startDateOf(target.after);
      if (after && before && before !== after) {
        return `Changed "Count metrics from" from ${dateText(before)} to ${dateText(after)}`;
      }
      if (after) return `Set "Count metrics from" to ${dateText(after)}`;
      return before
        ? `Cleared "Count metrics from" (was ${dateText(before)}; Metrics counts everything)`
        : 'Cleared "Count metrics from" (Metrics counts everything)';
    }
    case 'site_settings.zone_groups': {
      const boh = zoneList(target.to_boh);
      const foh = zoneList(target.to_foh);
      const parts: string[] = [];
      if (boh.length) parts.push(`${zonesSentence(boh)} to back of house`);
      if (foh.length) parts.push(`${zonesSentence(foh)} to front of house`);
      return parts.length ? `Moved ${parts.join('; ')}` : 'Changed zone groups';
    }
    case 'site_settings.robot_groups': {
      // The server writes both sides as display text (`from`/`to`).
      const from = typeof target.from === 'string' ? target.from : null;
      const to = typeof target.to === 'string' ? target.to : null;
      if (target.reset === true) return from ? `Reset robot groups to by type (was ${from})` : 'Reset robot groups to by type';
      if (from && to) return `Changed robot groups from ${from} to ${to}`;
      return to ? `Set robot groups to ${to}` : 'Changed robot groups';
    }
    default:
      return null;
  }
}

// ---- Schedules and blocked time (api-server 0.6.54) ---------------------------
//
// One row per write, with FLAT targets -- strings and numbers only, "none"
// for no end -- because the unlinked Activity view prints them as
// `key=value`. The schedule's own zone rides on the rows that carry a
// rule (create, retime, delete), so a rule is described on ITS clock;
// a single run (skip, move) and a blocked time carry only instants, and
// are printed the way the Timestamp column prints, on this browser's.

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function quoted(target: Record<string, unknown>): string {
  const name = str(target.name);
  return name ? `"${name}"` : 'a schedule';
}

function lowerFirst(s: string): string {
  return s ? s[0]!.toLowerCase() + s.slice(1) : s;
}

/** A stored rule in words, on its own zone ("every day at 09:00"), or
 *  null when the row does not carry enough to say it. */
function ruleWords(rule: unknown, dtstart: unknown, zone: unknown, until?: unknown): string | null {
  const r = str(rule);
  const start = asNumber(dtstart);
  const tz = str(zone);
  if (!r || start === null || !tz || !isKnownTimeZone(tz)) return null;
  const end = asNumber(until);
  try {
    return lowerFirst(describeRule(r, start, end, tz, { siteTimeZone: tz }));
  } catch {
    return null;
  }
}

/** The new end of an update row ("none" when it was cleared). The row
 *  carries no zone, so the instant is printed as the Timestamp column
 *  prints its own. */
function endWords(v: unknown): string {
  const ms = asNumber(v);
  return ms === null ? 'no end date now' : `now ends ${formatDateTime(ms)}`;
}

/** A retime's carry-over (api-server 0.6.54): the old runs before the
 *  change day it kept on a second schedule, "; 4 runs kept at the old
 *  time until 03/Nov/2026" -- on the OLD zone, the carry-over's. The
 *  second schedule's id is not printed: it names nothing to a reader. */
function keptWords(target: Record<string, unknown>): string {
  const n = asNumber(target.kept_runs) ?? 0;
  if (n <= 0) return '';
  const runs = `${n} run${n === 1 ? '' : 's'} kept at the old time`;
  const until = asNumber(target.kept_until_ms);
  const tz = str(target.from_timezone);
  if (until === null || !tz || !isKnownTimeZone(tz)) return `; ${runs}`;
  // The end of a day, as the server ends one; else cut at a dragged
  // run's own time, which is not kept.
  const end = endOn(until, tz);
  return end.startsWith('before ') ? `; ${runs} ${end}` : `; ${runs} until ${end}`;
}

/** An end on a zone, as `keptWords` says one: "03/Nov/2026" for one at
 *  the end of a day, else "before 04/Nov/2026, 09:00" -- a carry-over
 *  cut at a dragged run's own time, which it does not keep. */
function endOn(until: number, tz: string): string {
  const { hour, minute } = zonedParts(until, tz);
  return hour === 23 && minute === 59
    ? formatZonedDate(until, tz)
    : `before ${formatZonedDateTime(Math.ceil((until + 1) / 60_000) * 60_000, tz)}`;
}

/** The other rows of the plan a retime removed or cut short (api-server
 *  0.6.54, `_clip_audit`), which get no audit row of their own: `;
 *  another "Vacuum · L4-Lobby" (every day at 09:00, until 03/Nov/2026)
 *  was removed, its 4 runs replaced by the new time`, or `... now ends
 *  31/Oct/2026, 3 runs replaced by the new time`. Named by the first
 *  one's old rule, on its own zone, as the drawer named it before the
 *  save; its id is not printed. */
function clippedWords(target: Record<string, unknown>): string {
  const removed = asNumber(target.removed_schedules) ?? 0;
  const cut = asNumber(target.cut_short_schedules) ?? 0;
  if (removed + cut <= 0) return '';
  const name = quoted(target);
  const runs = asNumber(target.clipped_runs) ?? 0;
  const lost =
    runs > 0
      ? `, ${removed + cut === 1 && removed ? 'its ' : ''}${runs} run${runs === 1 ? '' : 's'} replaced by the new time`
      : '';
  if (removed + cut > 1) {
    const what = removed && cut ? 'removed or cut short' : removed ? 'removed' : 'cut short';
    return `; ${removed + cut} other ${name} schedules were ${what}${lost}`;
  }
  const rule = ruleWords(
    target.clipped_rule,
    target.clipped_dtstart_ms,
    target.clipped_timezone,
    target.clipped_from_until_ms,
  );
  const who = `another ${name}${rule ? ` (${rule})` : ''}`;
  if (removed) return `; ${who} was removed${lost}`;
  const until = asNumber(target.clipped_until_ms);
  const tz = str(target.clipped_timezone);
  const ends = until !== null && tz && isKnownTimeZone(tz) ? `now ends ${endOn(until, tz)}` : 'was cut short';
  return `; ${who} ${ends}${lost}`;
}

/** The other listed rows of the plan the same new details reached
 *  (`also_changed`, their ids), as a count: "; the other listed "Lobby"
 *  got the same change". */
function alsoWords(target: Record<string, unknown>): string {
  const ids = (str(target.also_changed) ?? '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
  if (ids.length === 0) return '';
  const name = quoted(target);
  return ids.length === 1
    ? `; the other listed ${name} got the same change`
    : `; the ${ids.length} other listed ${name} got the same change`;
}

/** The details an edit changed (`target.changed`), in words: "renamed
 *  from "Lobby AM"", "what it does", "now ends ...", "paused". A retime
 *  names them too: one save can move the time AND the place. */
function detailParts(target: Record<string, unknown>): string[] {
  const changed = (str(target.changed) ?? '')
    .split(',')
    .map((c) => c.trim())
    .filter((c) => c && c !== 'nothing');
  const parts: string[] = [];
  for (const c of changed) {
    if (c === 'name') {
      const from = str(target.from_name);
      parts.push(from ? `renamed from "${from}"` : 'renamed');
    } else if (c === 'end') {
      parts.push(endWords(target.to_until_ms));
    } else if (c === 'task') parts.push('what it does');
    // A level change, named on its own (api-server 0.6.56): who made a
    // plan High, or took it back, is what the trail is read for.
    else if (c === 'priority') {
      parts.push(isHighPriority(target.to_priority) ? 'now High priority' : 'now Normal priority');
    }
    else if (c === 'colour') parts.push('its colour');
    else if (c === 'paused' || c === 'resumed') parts.push(c);
  }
  return parts;
}

/** "; High priority" on a row that made a plan, or one run, High
 *  (api-server 0.6.56 writes `priority: 1` then, and nothing for Normal,
 *  which every plan is unless somebody chose). */
function highWords(target: Record<string, unknown>): string {
  return isHighPriority(target.priority) ? '; High priority' : '';
}

function scheduleLabel(action: string, target: Record<string, unknown>): string | null {
  const name = quoted(target);
  switch (action) {
    case 'schedule.create': {
      const rule = ruleWords(target.rule, target.dtstart_ms, target.timezone, target.until_ms);
      return `${rule ? `Created schedule ${name}: ${rule}` : `Created schedule ${name}`}${highWords(target)}`;
    }
    case 'schedule.update': {
      const parts = detailParts(target);
      return parts.length
        ? `Changed schedule ${name}: ${parts.join('; ')}${alsoWords(target)}`
        : `Saved schedule ${name} (no change)`;
    }
    case 'schedule.retime': {
      const from = ruleWords(target.from_rule, target.from_dtstart_ms, target.from_timezone);
      const to = ruleWords(target.to_rule, target.to_dtstart_ms, target.to_timezone);
      const first = asNumber(target.first_run_ms) ?? asNumber(target.to_dtstart_ms);
      const tz = str(target.to_timezone);
      const when =
        first !== null && tz && isKnownTimeZone(tz) ? `, from ${formatZonedDate(first, tz)}` : '';
      const head =
        from && to
          ? `Changed when ${name} runs: ${from} → ${to}${when}`
          : `Changed when ${name} runs${when}`;
      const skips = asNumber(target.dropped_skips) ?? 0;
      const moves = asNumber(target.dropped_moves) ?? 0;
      const dropped =
        skips + moves > 0
          ? `; ${[
              skips ? `${skips} skipped day${skips === 1 ? '' : 's'}` : null,
              moves ? `${moves} moved run${moves === 1 ? '' : 's'}` : null,
            ]
              .filter(Boolean)
              .join(' and ')} after it no longer ${skips + moves === 1 ? 'applies' : 'apply'}`
          : '';
      // What else the same save changed: the place, the name, the end.
      const also = detailParts(target);
      const details = also.length ? `; also ${also.join('; ')}` : '';
      return `${head}${dropped}${keptWords(target)}${clippedWords(target)}${details}${
        also.length ? alsoWords(target) : ''
      }`;
    }
    // One row per schedule: "Pause both" writes two of one name, told
    // apart by the time each runs (api-server 0.6.54; an older row has
    // no rule and reads by its name alone).
    case 'schedule.pause':
    case 'schedule.resume': {
      const verb = action === 'schedule.pause' ? 'Paused' : 'Resumed';
      const rule = ruleWords(target.rule, target.dtstart_ms, target.timezone, target.until_ms);
      return rule ? `${verb} schedule ${name} (${rule})` : `${verb} schedule ${name}`;
    }
    case 'schedule.skip': {
      const at = asNumber(target.original_start_ms);
      return at === null
        ? `Skipped a run of ${name}`
        : `Skipped the ${formatDateTime(at)} run of ${name}`;
    }
    case 'schedule.move': {
      const at = asNumber(target.original_start_ms);
      const to = asNumber(target.replacement_start_ms);
      const run = at === null ? 'a run' : `the ${formatDateTime(at)} run`;
      const high = highWords(target);
      if (to !== null) return `Moved ${run} of ${name} to ${formatDateTime(to)}${high}`;
      return target.task === 'replaced'
        ? `Changed what ${run} of ${name} does${high}`
        : `Changed ${run} of ${name}${high}`;
    }
    case 'schedule.delete': {
      const rule = ruleWords(target.rule, target.dtstart_ms, target.timezone);
      return rule ? `Deleted schedule ${name} (${rule})` : `Deleted schedule ${name}`;
    }
    case 'maintenance.create':
    case 'maintenance.delete': {
      const from = asNumber(target.starts_at_ms);
      const to = asNumber(target.ends_at_ms);
      const reason = str(target.reason);
      const span = from !== null && to !== null ? ` ${formatDateTime(from)} – ${formatDateTime(to)}` : '';
      const why = reason ? ` (${reason})` : '';
      return action === 'maintenance.create'
        ? `Blocked time${span}${why}`
        : `Removed blocked time${span}${why}`;
    }
    case 'maintenance.update': {
      // Changing a window can end it early and release dispatch, so the
      // row says from what to what.
      const span = (from: unknown, to: unknown): string | null => {
        const a = asNumber(from);
        const b = asNumber(to);
        return a !== null && b !== null ? `${formatDateTime(a)} – ${formatDateTime(b)}` : null;
      };
      const was = span(target.from_starts_at_ms, target.from_ends_at_ms);
      const now = span(target.to_starts_at_ms, target.to_ends_at_ms);
      const reason = str(target.to_reason);
      const why = reason ? ` (${reason})` : '';
      if (was && now && was !== now) return `Changed blocked time ${was} to ${now}${why}`;
      return now ? `Changed blocked time ${now}${why}` : `Changed blocked time${why}`;
    }
    default:
      return null;
  }
}

// ---- Commands to robots, doors and lifts (2026-10-31) ------------------------
//
// A command's row carries the capability it was authorised as and the
// command's own target (`commands.py::_authorize_and_publish`). Each says
// what was ASKED, "Asked to ...": the row is written whether the command
// was refused, went unanswered or was done, and the Outcome column says
// which. Buttons are named as they read on screen (Kill, Recommission,
// Declare manually open). Only what the console's controls, the calendar
// and the platform's own fire recovery send; the rest keep their code.
// `task.retire` is not here: it is written to the admin trail, which this
// screen does not read.

/** What a dispatched task does and where, as the calendar titles a run:
 *  "Vacuum · L4-Lobby". Null when the row carries no category. */
export function dispatchedTaskTitle(target: Record<string, unknown>): string | null {
  const category = str(target.category);
  if (!category) return null;
  const request = {
    ...target,
    phases: Array.isArray(target.phases) ? target.phases : [],
  } as unknown as TaskRequest;
  const lane = categoryOf(category, category === 'clean' ? cleanModeOfRequest(request) : undefined);
  return titleOf(CATEGORY_LABEL[lane], locationFromRequest(category, request)?.text ?? null);
}

/** Where a dispatch came from, when not the New task form. */
function dispatchSource(target: Record<string, unknown>): string {
  if (target.requested_via === 'calendar') {
    // The calendar's own dispatches carry the schedule's name.
    const schedule = str(target.requester);
    return schedule ? `, from schedule "${schedule}"` : ', from the calendar';
  }
  return target.requested_via === 'favorite' ? ', from a favourite' : '';
}

const TASK_VERB: Readonly<Record<string, string>> = {
  'task.cancel': 'cancel',
  'task.kill': 'kill',
  'task.resume': 'resume',
  'task.interrupt': 'interrupt',
};

/** A lift rule's scope, in Akash's words. */
function liftScopeWords(scope: unknown): string {
  return scope === 'between_floors' ? 'travel between floors' : 'robot use';
}

function liftWindowWords(target: Record<string, unknown>): string {
  const lift = str(target.lift) ?? 'a lift';
  const kind =
    target.kind === 'allow_travel'
      ? `Allow travel between floors on ${lift}`
      : `Block ${lift} from robot use`;
  const at = asNumber(target.starts_at_ms);
  const rule = ruleWords(target.rrule, target.starts_at_ms, target.timezone);
  const when = rule ?? (at !== null ? formatDateTime(at) : null);
  const reason = str(target.reason);
  return `${kind}${when ? `, ${when}` : ''}${reason ? ` (${reason})` : ''}`;
}

function liftRuleLabel(action: string, target: Record<string, unknown>): string | null {
  const lift = str(target.lift) ?? 'a lift';
  switch (action) {
    case 'lift.rule_hold': {
      const scope = target.scope === 'between_floors' ? `travel between floors on ${lift}` : `${lift} from robot use`;
      const at = asNumber(target.until_ms);
      const until =
        target.until === 'next_change'
          ? ' until the next calendar change'
          : at !== null
            ? ` until ${formatDateTime(at)}`
            : ' until released';
      const cut = str(target.confirmed_cut_off);
      return `Blocked ${scope}${until}${withReason(target)}${cut ? `; confirmed robots on ${cut} can't get back to a charger` : ''}`;
    }
    case 'lift.rule_release': {
      const by = str(target.held_by);
      return `Released the block on ${liftScopeWords(target.scope)} of ${lift}${by ? `, set by ${by}` : ''}${target.lift_rules_turned_off === true ? ', because lift rules were turned off for this site' : ''}`;
    }
    case 'lift.rule_hold_ended':
      return `The block on ${liftScopeWords(target.scope)} of ${lift} ended`;
    case 'lift.rule_breaker_reset':
      return `Reset ${liftScopeWords(target.scope)} of ${lift} after it changed too many times`;
    case 'lift.rule_window_create':
      return `Put on the calendar: ${liftWindowWords(target)}`;
    case 'lift.rule_window_update': {
      const skipped = asNumber(target.skipped_ms);
      return skipped !== null
        ? `Skipped the ${formatDateTime(skipped)} run: ${liftWindowWords(target)}`
        : `Changed on the calendar: ${liftWindowWords(target)}`;
    }
    case 'lift.rule_window_delete':
      return `Took off the calendar: ${liftWindowWords(target)}`;
    case 'lift.apply_rules':
      return 'Sent the lift rules to the robots';
    default:
      return null;
  }
}

function withReason(target: Record<string, unknown>): string {
  const reason = str(target.reason);
  return reason ? ` (${reason})` : '';
}

function commandLabel(action: string, target: Record<string, unknown>): string | null {
  const robot = str(target.robot);
  const verb = TASK_VERB[action];
  // A task by its robot when the row names one (the platform's own
  // cancels do); an operator's carries only the task's id, which the
  // Target column shows.
  if (verb) return `Asked to ${verb} ${robot ? `${robot}'s task` : 'a task'}`;
  switch (action) {
    case 'task.dispatch': {
      const title = dispatchedTaskTitle(target);
      const on = robot ? ` on ${robot}` : '';
      return `Asked for a new task${title ? `: ${title}` : ''}${on}${dispatchSource(target)}`;
    }
    case 'robot.commission':
      return `Asked to recommission ${robot ?? 'a robot'}`;
    case 'robot.decommission':
      return `Asked to decommission ${robot ?? 'a robot'}`;
    case 'robot.set_idle_behavior': {
      if (typeof target.enabled !== 'boolean') return null;
      const onOff = target.enabled ? 'on' : 'off';
      return `Asked to switch automatic charging ${onOff} for ${robot ?? 'a robot'}${withReason(target)}`;
    }
    case 'robot.fire_relocalize':
      // "Confirm floor and release": the person's own word, as "Confirmed
      // routine check done" is; whether the release landed is the Outcome.
      return `Confirmed ${robot ? `${robot}'s` : "a robot's"} floor after the fire alarm`;
    case 'lift.arm_writes': {
      if (typeof target.armed !== 'boolean') return null;
      return `Asked to ${target.armed ? 'arm' : 'disarm'} the lifts for robots${withReason(target)}`;
    }
    case 'lift.manual_call': {
      const lift = str(target.lift) ?? 'a lift';
      const floor = str(target.floor);
      return floor ? `Asked to call ${lift} to ${floor}` : `Asked to call ${lift}`;
    }
    case 'lift.manual_close':
      return `Asked to cancel the call for ${str(target.lift) ?? 'a lift'}`;
    // The robot-held car's verbs, by the names the lift panel gives them
    // now batch B has settled them (review BB-B-5, 2026-10-31): Recover,
    // End lift session, the confirm of a step the lift cannot see, and
    // the abort.
    case 'lift.recover':
      return `Asked to recover the stuck robot from ${str(target.lift) ?? 'a lift'}${withReason(target)}`;
    case 'lift.end_session':
      return `Asked to end the lift session on ${str(target.lift) ?? 'a lift'}${withReason(target)}`;
    case 'lift.advance':
      return `Confirmed a lift step for ${str(target.lift) ?? 'a lift'}`;
    case 'lift.abort':
      return `Asked to stop the ride on ${str(target.lift) ?? 'a lift'}${withReason(target)}`;
    // The doors' counterpart of `lift.arm_writes` (doors.py), which had
    // no sentence: its row read the code and "armed=true" (BB-B-5).
    case 'door.arm_writes': {
      if (typeof target.armed !== 'boolean') return null;
      return `Asked to ${target.armed ? 'arm' : 'disarm'} the doors for robots${withReason(target)}`;
    }
    case 'door.open':
    case 'door.close':
      return `Asked to ${action === 'door.open' ? 'open' : 'close'} ${str(target.door) ?? 'a door'}`;
    case 'door.declare_open': {
      const ttl = asNumber(target.ttl_s);
      const minutes = ttl !== null && ttl > 0 ? ` for ${Math.max(1, Math.round(ttl / 60))} min` : '';
      return `Asked to declare ${str(target.door) ?? 'a door'} manually open${minutes}`;
    }
    case 'door.revoke_override':
      return `Asked to revoke the override on ${str(target.door) ?? 'a door'}`;
    default:
      return null;
  }
}

/** The row's own sentence, or null to show the code. */
export function auditActionLabel(entry: Pick<AuditEntry, 'action' | 'target'>): string | null {
  switch (entry.action) {
    case 'role_preview.enter':
      return `Viewed console as ${previewedLabel(entry.target)}`;
    case 'role_preview.exit':
      return `Stopped viewing as ${previewedLabel(entry.target)}`;
    case 'role_preview.sign_out':
      return `Stopped viewing as ${previewedLabel(entry.target)} (signed out)`;
    case 'role_preview.dropped':
      return `Preview as ${previewedLabel(entry.target)} ended`;
    // Internal key unchanged (`dustbag`); the hotel calls it a routine
    // check (Akash, 2026-10-22).
    case 'robot.confirm_dustbag':
      return 'Confirmed routine check done';
    default:
      return (
        scheduleLabel(entry.action, entry.target ?? {}) ??
        siteSettingsLabel(entry.action, entry.target ?? {}) ??
        liftRuleLabel(entry.action, entry.target ?? {}) ??
        commandLabel(entry.action, entry.target ?? {})
      );
  }
}

/** One label per ACTION CODE, for the action filter (which filters by
 *  code, so it cannot name the role a particular row previewed). */
export function auditActionFilterLabel(action: string): string {
  switch (action) {
    case 'role_preview.enter':
      return 'Viewed console as a role';
    case 'role_preview.exit':
      return 'Stopped viewing as a role';
    case 'role_preview.sign_out':
      return 'Stopped viewing as a role (signed out)';
    case 'role_preview.dropped':
      return 'Role preview ended';
    case 'robot.confirm_dustbag':
      return 'Confirmed routine check done';
    case 'site_settings.labour_rate':
      return 'Changed the labour rate';
    case 'site_settings.cleaning_targets':
      return 'Changed cleaning targets';
    case 'site_settings.zone_groups':
      return 'Changed zone groups';
    case 'site_settings.metrics_start_date':
      return 'Changed "Count metrics from"';
    case 'site_settings.robot_groups':
      return 'Changed robot groups';
    case 'schedule.create':
      return 'Created a schedule';
    case 'schedule.update':
      return "Changed a schedule's details";
    case 'schedule.retime':
      return "Changed a schedule's time";
    case 'schedule.pause':
      return 'Paused a schedule';
    case 'schedule.resume':
      return 'Resumed a schedule';
    case 'schedule.skip':
      return 'Skipped a scheduled run';
    case 'schedule.move':
      return 'Moved a scheduled run';
    case 'schedule.delete':
      return 'Deleted a schedule';
    case 'maintenance.create':
      return 'Blocked time';
    case 'maintenance.update':
      return 'Changed blocked time';
    case 'maintenance.delete':
      return 'Removed blocked time';
    case 'task.dispatch':
      return 'Asked for a new task';
    case 'task.cancel':
      return 'Asked to cancel a task';
    case 'task.kill':
      return 'Asked to kill a task';
    case 'task.resume':
      return 'Asked to resume a task';
    case 'task.interrupt':
      return 'Asked to interrupt a task';
    case 'robot.commission':
      return 'Asked to recommission a robot';
    case 'robot.decommission':
      return 'Asked to decommission a robot';
    case 'robot.set_idle_behavior':
      return 'Asked to switch automatic charging on or off';
    case 'robot.fire_relocalize':
      return "Confirmed a robot's floor after the fire alarm";
    case 'lift.rule_hold':
      return 'Blocked a lift for robots';
    case 'lift.rule_release':
      return 'Released a block on a lift';
    case 'lift.rule_hold_ended':
      return 'A block on a lift ended';
    case 'lift.rule_breaker_reset':
      return 'Reset a lift that changed too many times';
    case 'lift.rule_window_create':
      return 'Put a lift rule on the calendar';
    case 'lift.rule_window_update':
      return 'Changed a lift rule on the calendar';
    case 'lift.rule_window_delete':
      return 'Took a lift rule off the calendar';
    case 'lift.apply_rules':
      return 'Sent the lift rules to the robots';
    case 'lift.arm_writes':
      return 'Asked to arm or disarm the lifts';
    case 'lift.manual_call':
      return 'Asked to call a lift';
    case 'lift.manual_close':
      return 'Asked to cancel a lift call';
    case 'lift.recover':
      return 'Asked to recover a stuck robot from a lift';
    case 'lift.end_session':
      return 'Asked to end a lift session';
    case 'lift.advance':
      return 'Confirmed a lift step';
    case 'lift.abort':
      return 'Asked to stop a lift ride';
    case 'door.arm_writes':
      return 'Asked to arm or disarm the doors';
    case 'door.open':
      return 'Asked to open a door';
    case 'door.close':
      return 'Asked to close a door';
    case 'door.declare_open':
      return 'Asked to declare a door manually open';
    case 'door.revoke_override':
      return 'Asked to revoke a door override';
    default:
      return action;
  }
}
